<template>
  <v-card flat>
    <v-col cols="12" class="px-0">
      <v-btn color="primary" icon @click="beforeLeave">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-h6 mr-2" style="vertical-align: middle">
        {{ $t('message.pages.video.bigPlayButton') }}
      </span>
    </v-col>

    <v-row no-gutters class="py-0 px-2">
      <v-col cols="12">
        <v-card flat>
          <span>
            {{ $t('message.pages.video.messageBigPlayButton') }}
          </span>
        </v-card>
      </v-col>

      <v-col cols="12" class="mt-3 mb-10">
        <v-row no-gutters>
          <v-switch
            class="mt-0"
            v-model="controls"
            @click="changePlayer"
            :label="$t('message.pages.video.bigPlayButton')"
            value="play-large"
            hide-details
            :disabled="!hasEditVideoPerm"
          ></v-switch>
        </v-row>
      </v-col>
      <v-scroll-x-transition mode="out-in" hide-in-live>
        <v-col v-if="isBigPlayButtonEnabled">
          <v-slider
            v-model="bigPlayButtonSize"
            class="mt-0 range-slider-big-play-button"
            :label="$t('message.pages.video.bigPlayButtonSize') + ':'"
            color="primary"
            thumb-color="primary"
            thumb-label="always"
            :max="150"
            :min="48"
            hide-details
            @change="changePlayer"
            :disabled="!hasEditVideoPerm"
          >
            <template v-slot:thumb-label> {{ bigPlayButtonSize }}px </template>
          </v-slider>
        </v-col>
      </v-scroll-x-transition>
    </v-row>

    <v-row no-gutters v-if="hasEditVideoPerm">
      <v-col cols="12">
        <v-card-actions class="px-8 pt-9 pb-6">
          <v-spacer></v-spacer>
          <v-btn
            class="mr-2 px-4"
            text
            :disabled="loadingButtonSave || withoutChanges"
            @click="handleUndoChanges"
          >
            {{ $t('message.pages.video.discardChanges') }}
          </v-btn>
          <v-btn
            class="px-6"
            color="primary"
            @click="handleUpdateBigPlayButton"
            :disabled="loadingButtonSave || withoutChanges"
            :loading="loadingButtonSave"
          >
            {{ $t('message.pages.video.publish') }}
          </v-btn>
        </v-card-actions>
      </v-col>
    </v-row>

    <!-- dialog -->
    <unsaved-changes-modal />
  </v-card>
</template>

<script>
// external imports
import { mapActions, mapMutations, mapState } from 'vuex'
import _ from 'lodash'

// internal imports
import i18n from '@/translations/lang'

// API

// Components
import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue'

export default {
  name: 'VideoPlayerBigPlayButton',
  components: {
    UnsavedChangesModal,
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    liveVideoId: {
      required: false,
    },
    hasEdit: {
      type: Array,
    },
  },
  data: () => ({
    bigPlayButtonSize: 100,
    controls: [],
    max: 7,
    loadingButtonSave: false,
    videoPlayer: {},
    profileId: null,
    profileConfig: {},
    withoutChanges: true,
    videoId: '',
    initialConfig: {},
  }),
  async mounted() {
    this.videoId = this.liveVideoId ? this.liveVideoId : this.$route.params.id
    this.profileId = this.$store.state.video.video.config?.profile
    this.videoPlayer = this.$store.state.video.player.config
    this.controls = this.$store.state.video.profile.controls
      ? this.$store.state.video.profile.controls
      : [
          'play-large',

          'rewind',

          'play',

          'fast-forward',

          'progress',

          'current-time',

          'volume',

          'captions',

          'settings',

          'pip',

          'fullscreen',
        ]
    this.bigPlayButtonSize = this.$store.state.video.profile?.general
      ?.bigPlayButtonSize
      ? this.$store.state.video.profile?.general?.bigPlayButtonSize
      : 100
    this.setBigPlayButtonIconSize()
    this.initialConfig.bigPlayButtonSize = _.cloneDeep(this.bigPlayButtonSize)
    this.initialConfig.controls = _.cloneDeep(this.controls)
    this.hasEdit.push(this.checkChanges)
    this.$root.$on('discardChanges', () => {
      this.handleUndoChanges()
      this.handleBack()
    })
  },
  watch: {
    '$store.state.video.profile': {
      handler() {
        this.withoutChanges = this.checkChanges()
      },
      deep: true,
    },
  },
  computed: {
    ...mapState({
      profile: (state) => state.video.profile,
      video: (state) => state.video.video,
      profile_id: (state) => state.video.profile_id,
      internalError: (state) => state.video.internalError,
    }),
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    isBigPlayButtonEnabled() {
      if (this.controls.indexOf('play-large') !== -1) return true
      return false
    },
  },
  methods: {
    ...mapActions('video', [
      'updateProfile',

      'createProfile',

      'setProfileToVideos',
    ]),
    ...mapMutations('video', [
      'UPDATE_VIDEO_PROFILE',
      'UPDATE_VIDEO_PROFILE_ID',
      'UPDATE_STATUS_ERROR',
    ]),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    async changePlayer() {
      this.organizeControls()
      this.profile.controls = this.controls
      this.profile.general.bigPlayButtonSize = this.bigPlayButtonSize
      this.setBigPlayButtonIconSize()
      this.UPDATE_STATUS_ERROR(false)
      this.UPDATE_VIDEO_PROFILE(this.profile)
      this.$root.$emit('updateEmbedPreviewChange')
    },
    setBigPlayButtonIconSize() {
      if (this.bigPlayButtonSize > 67) {
        this.profile.general.bigPlayButtonIconSize = Math.round(
          this.bigPlayButtonSize / 1.75
        )
      } else {
        this.profile.general.bigPlayButtonIconSize =
          this.bigPlayButtonSize / 2 - 9.5
      }
    },
    organizeControls() {
      const organizedControls = [
        'play-large',

        'rewind',

        'play',

        'fast-forward',

        'progress',

        'current-time',

        'volume',

        'captions',

        'settings',

        'pip',

        'fullscreen',
      ]
      this.controls.sort(function (a, b) {
        return organizedControls.indexOf(a) - organizedControls.indexOf(b)
      })
      return this.controls
    },
    handleUndoChanges() {
      this.bigPlayButtonSize = _.cloneDeep(this.initialConfig.bigPlayButtonSize)
      this.controls = _.cloneDeep(this.initialConfig.controls)
      this.changePlayer()
    },
    checkChanges() {
      const variation1 = _.isEqual(
        this.profile.general.bigPlayButtonSize,
        this.initialConfig.bigPlayButtonSize
      )
      const variation2 = _.isEqual(
        this.profile.controls,
        this.initialConfig.controls
      )

      return variation1 && variation2
    },
    beforeLeave() {
      if (!this.checkChanges()) {
        this.$root.$emit('unsavedChangesModal')
      } else {
        this.UPDATE_STATUS_ERROR(false)
        this.handleBack()
      }
    },
    handleSnackbar(item) {
      if (!item) {
        item = i18n.t('message.changesSaved')
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: item,
      })
    },
    async handleUpdateBigPlayButton() {
      this.loadingButtonSave = true
      this.organizeControls()
      const general = this.$store.state.video.profile?.general
      try {
        if (!this.video.config.profile) {
          await this.createProfile(this.videoId)
          this.video.config.profile = this.profile_id
          this.profileId = this.profile_id
          await this.setProfileToVideos([this.profileId, this.videoId])
        }
        await this.updateProfile([
          this.profileId,
          { controls: this.controls, general },
        ])
        this.initialConfig.bigPlayButtonSize = this.bigPlayButtonSize
        this.initialConfig.controls = _.cloneDeep(this.controls)
        if (!this.internalError) {
          this.handleSnackbar(i18n.t('message.pages.video.changesSaved'))
          this.initialConfig.bigPlayButtonSize = _.cloneDeep(
            this.bigPlayButtonSize
          )
          this.initialConfig.controls = _.cloneDeep(this.controls)
          this.withoutChanges = true
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingButtonSave = false
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
  },
}
</script>

<style>
.range-slider-big-play-button .v-slider__thumb-label {
  height: 40px !important;
  width: 40px !important;
}
</style>
